import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { API } from '../../services/api';
const api = new API();

import { changeItemOwner } from '../itemOwner/itemOwnersSlice';

/**
 * Removes fields from family data that are not editable by user role.
 * Backend will refuse to save if we try to save these fields without permissions.
 *
 * @param {object} itemFamilyData
 * @param {object} canEditRoleBasedFields - {fieldId: boolean}
 * @returns
 */
function cleanData(itemFamilyData, canEditRoleBasedFields = {}) {
  const cleanedData = {...itemFamilyData};

  Object.keys(canEditRoleBasedFields).forEach(fieldId => {
    if(canEditRoleBasedFields[fieldId] === false) {
      delete cleanedData[fieldId];
    }
  });

  return cleanedData;
}

export const fetchItemFamily = createAsyncThunk(
  'itemFamily/get',
  async (data, thunkAPI) => {
    try {
      const { ownerId, itemFamilyId } = data;
      const response = await api.getItemFamily(ownerId, itemFamilyId);

      if(response.status === 'ok') {
        return response.result;
      } else {
        return thunkAPI.rejectWithValue(response);
      }
    } catch(e) {
      // System error or status code >= 500
      console.error('ERROR: itemFamily/get', e.message);

      return thunkAPI.rejectWithValue('INTERNAL_SERVER_ERROR');
    }
  }
);

export const saveItemFamilyDraft = createAsyncThunk(
  'itemFamily/save/draft',
  async (data, thunkAPI) => {
    try {
      const { itemOwnerId, itemFamilyId, itemFamilyData, canEditRoleBasedFields } = data;

      const cleanedData = cleanData(itemFamilyData, canEditRoleBasedFields);
      const response = await api.saveDraftItemFamily(itemOwnerId, itemFamilyId, cleanedData);

      if(response.status === 'ok') {
        return itemFamilyData;
      } else {
        return thunkAPI.rejectWithValue(response);
      }
    } catch(e) {
      // System error or status code >= 500
      console.error('ERROR: itemFamily/get', e.message);

      return thunkAPI.rejectWithValue('INTERNAL_SERVER_ERROR');
    }
  }
);

export const saveItemFamilyPublished = createAsyncThunk(
  'itemFamily/save/published',
  async (data, thunkAPI) => {
    try {
      const { itemOwnerId, itemFamilyId, itemFamilyData, canEditRoleBasedFields } = data;

      const cleanedData = cleanData(itemFamilyData, canEditRoleBasedFields);
      const response = await api.savePublishedItemFamily(itemOwnerId, itemFamilyId, cleanedData);

      if(response.status === 'ok') {
        return itemFamilyData;
      } else {
        return thunkAPI.rejectWithValue(response);
      }
    } catch(e) {
      // System error or status code >= 500
      console.error('ERROR: itemFamily/get', e.message);

      return thunkAPI.rejectWithValue('INTERNAL_SERVER_ERROR');
    }
  }
);

export const publishItemFamily = createAsyncThunk(
  'itemFamily/publish',
  async (data, thunkAPI) => {
    try {
      const { itemOwnerId, itemFamilyId, itemFamilyData, canEditRoleBasedFields } = data;

      const cleanedData = cleanData(itemFamilyData, canEditRoleBasedFields);
      const response = await api.publishItemFamily(itemOwnerId, itemFamilyId, cleanedData);

      if(response.status === 'ok') {
        return itemFamilyData;
      } else {
        return thunkAPI.rejectWithValue(response);
      }
    } catch(e) {
      // System error or status code >= 500
      console.error('ERROR: itemFamily/publish', e.message);

      return thunkAPI.rejectWithValue('INTERNAL_SERVER_ERROR');
    }
  }
)

export const createItemFamily = createAsyncThunk(
  'itemFamily/create',
  async (data, thunkAPI) => {
    try {
      const { itemOwnerId, itemFamilyData } = data;
      const response = await api.createItemFamily(itemOwnerId, itemFamilyData);

      if(response.status === 'ok') {
        return response.result;
      } else {
        return thunkAPI.rejectWithValue(response);
      }
    } catch(e) {
      console.error('ERROR: itemFamily/create', e.message);
      return thunkAPI.rejectWithValue('INTERNAL_SERVER_ERROR');
    }
  }
);

export const fetchItemFamilyAwards = createAsyncThunk(
  'itemFamily/awards/get',
  async (data, thunkAPI) => {
    try {
      const { itemOwnerId, itemFamilyId } = data;
      const response = await api.getFamilyTyreAwards(itemOwnerId, itemFamilyId);

      if(response.status === 'ok') {
        return response.result.awards;
      } else {
        return thunkAPI.rejectWithValue(response);
      }
    } catch(e) {
      console.error('ERROR: itemFamily/awards/get', e.message);
      return thunkAPI.rejectWithValue('INTERNAL_SERVER_ERROR');
    }
  }
);

export const fetchItemFamilyReviews = createAsyncThunk(
  'itemFamily/reviews/get',
  async (data, thunkAPI) => {
    try {
      const { itemOwnerId, itemFamilyId } = data;
      const response = await api.getFamilyReviews(itemOwnerId, itemFamilyId);

      if(response.status === 'ok') {
        return response.result;
      } else {
        return thunkAPI.rejectWithValue(response);
      }
    } catch(e) {
      console.error('ERROR: itemFamily/reviews/get', e.message);
      return thunkAPI.rejectWithValue('INTERNAL_SERVER_ERROR');
    }
  }
);

const initialState = {
  draft: {},
  published: {},
  master: {},
  awards: [],
  reviews: [],
  equalStatus: false,
  canEdit: false, // Can edit the item family
  canEditRoleBasedFields: [], // Some fields need to be editable based on role
  fetchStatus: 'idle',
  fetchError: null,
  fetchAwardsStatus: 'idle',
  fetchAwardsError: null,
  fetchReviewsStatus: 'idle',
  fetchReviewsError: null,
  createStatus: 'idle',
  createError: null,
  saveStatus: 'idle',
  saveError: null
};

export const itemFamilySlice = createSlice({
  name: 'itemFamily',
  initialState,
  reducers: {
    clearSaveStatus: (state, action) => {
      state.saveStatus = 'idle';
    },
    clearCreateStatus: (state, action) => {
      state.createStatus = 'idle';
    },
    resetStatus: (state, action) => {
      return initialState;
    }
  },
  extraReducers: {
    // Add reducers for additional action types here, and handle loading state as needed
    [fetchItemFamily.fulfilled]: (state, action) => {
      state.fetchStatus = 'succeeded';
      state.fetchError = null;

      state.draft = action.payload.itemFamilyDraft || {itemLines: []};
      state.published = action.payload.itemFamilyPublished?.code ? action.payload.itemFamilyPublished : {};
      state.master = action.payload.masterItemFamily || {};
      state.equalStatus = action.payload.equalFamilyStatus;
      state.canEdit = action.payload.canEdit;
      state.canEditRoleBasedFields = action.payload.canEditRoleBasedFields;
    },
    [fetchItemFamily.pending]: (state, action) => {
      state.fetchStatus = 'loading';
      state.fetchError = null;
    },
    [fetchItemFamily.rejected]: (state, action) => {
      state.fetchStatus = 'failed';
      state.fetchError = action.payload.messages.join(', ');
    },

    [saveItemFamilyDraft.fulfilled]: (state, action) => {
      state.saveStatus = 'succeeded';
      state.saveError = null;
      state.draft = action.payload;
      state.equalStatus = false;
    },
    [saveItemFamilyDraft.pending]: (state, action) => {
      state.saveStatus = 'loading';
      state.saveError = null;
    },
    [saveItemFamilyDraft.rejected]: (state, action) => {
      state.saveStatus = 'failed';

      if(typeof action.payload === 'string') {
        state.saveError = {
          message: action.payload
        };
      } else {
        state.saveError = {
          message: action.payload.messages.join(', '),
          fields: action.payload.result.errors
        };
      }
    },

    [saveItemFamilyPublished.fulfilled]: (state, action) => {
      state.saveStatus = 'succeeded';
      state.saveError = null;
      state.published = action.payload;
      state.equalStatus = false;
    },
    [saveItemFamilyPublished.pending]: (state, action) => {
      state.saveStatus = 'loading';
      state.saveError = null;
    },
    [saveItemFamilyPublished.rejected]: (state, action) => {
      state.saveStatus = 'failed';

      if(typeof action.payload === 'string') {
        state.saveError = {
          message: action.payload
        };
      } else {
        state.saveError = {
          message: action.payload.messages.join(', '),
          fields: action.payload.result.errors
        };
      }
    },

    [publishItemFamily.fulfilled]: (state, action) => {
      state.saveStatus = 'succeeded';
      state.saveError = null;
      state.published = action.payload;
      state.draft = action.payload;
      state.equalStatus = true;
    },
    [publishItemFamily.pending]: (state, action) => {
      state.saveStatus = 'loading';
      state.saveError = null;
    },
    [publishItemFamily.rejected]: (state, action) => {
      state.saveStatus = 'failed';
      console.log('action', action);

      let message = 'INTERNAL_SERVER_ERROR';

      if(typeof action.payload === 'string') {
        // error message
        message = action.payload;
      } else if(action.payload.messages.length) {
        message = action.payload.messages.join(', ');
      }

      state.saveError = {
        message: message
      };
    },

    [createItemFamily.fulfilled]: (state, action) => {
      state.createStatus = 'succeeded';
      state.createError = null;
      state.draft.code = action.payload;
      state.equalStatus = false;
    },
    [createItemFamily.pending]: (state, action) => {
      state.createStatus = 'loading';
      state.createError = null;
    },
    [createItemFamily.rejected]: (state, action) => {
      let message = 'INTERNAL_SERVER_ERROR';

      if(typeof action.payload === 'string') {
        // error message
        message = action.payload;
      } else if(action.payload.messages.length) {
        message = action.payload.messages.join(', ');
      }

      state.createStatus = 'failed';
      state.createError = {
        message: message,
        fields: action.payload.result?.errors
      };
    },

    [fetchItemFamilyAwards.fulfilled]: (state, action) => {
      state.fetchAwardsStatus = 'succeeded';
      state.fetchAwardsError = null;
      state.awards = action.payload;
    },
    [fetchItemFamilyAwards.pending]: (state, action) => {
      state.fetchAwardsStatus = 'loading';
      state.fetchAwardsError = null;
    },
    [fetchItemFamilyAwards.rejected]: (state, action) => {
      state.fetchAwardsStatus = 'failed';

      let message = '';
      if(typeof action.payload === 'string') {
        message = action.payload;
      } else {
        message = action.payload.messages.join(', ');
      }

      state.fetchAwardsError = {
        message: message
      };
    },

    [fetchItemFamilyReviews.fulfilled]: (state, action) => {
      state.fetchReviewsStatus = 'succeeded';
      state.fetchReviewsError = null;
      state.reviews = action.payload;
    },
    [fetchItemFamilyReviews.pending]: (state, action) => {
      state.fetchReviewsStatus = 'loading';
      state.fetchReviewsError = null;
    },
    [fetchItemFamilyReviews.rejected]: (state, action) => {
      state.fetchReviewsStatus = 'failed';

      let message = '';
      if(typeof action.payload === 'string') {
        message = action.payload;
      } else {
        message = action.payload.messages.join(', ');
      }

      state.fetchReviewsError = {
        message: message
      };
    },

    [changeItemOwner.fulfilled]: (state, action) => {
      // Clear state if owner changes
      return initialState;
    }
  }
});

export const { clearSaveStatus, clearCreateStatus, resetStatus } = itemFamilySlice.actions;

export const selectItemFamily = state => state.itemFamily;

export const selectFetchStatus = state => ({
  fetchStatus: state.itemFamily.fetchStatus,
  fetchError: state.itemFamily.fetchError,
  draft: state.itemFamily.draft,
  published: state.itemFamily.published
});

export const selectCreateStatus = state => ({
  status: state.itemFamily.createStatus,
  error: state.itemFamily.createError,
  itemFamily: state.itemFamily.draft
});

export const selectMaster = state => state.itemFamily.master;
export const selectEqualStatus = state => state.itemFamily.equalStatus;
export const selectAccess = state => state.itemFamily.canEdit;
export const selectAccessToRoleBasedFields = state => state.itemFamily.canEditRoleBasedFields;

export const selectSaveStatus = state => ({
  saveStatus: state.itemFamily.saveStatus,
  saveError: state.itemFamily.saveError,
});

export const selectAwards = state => ({
  awardsStatus: state.itemFamily.fetchAwardsStatus,
  awardsError: state.itemFamily.fetchAwardsError,
  awards: state.itemFamily.awards
});

export const selectReviews = state => ({
  reviewsStatus: state.itemFamily.fetchReviewsStatus,
  reviewsError: state.itemFamily.fetchReviewsError,
  reviews: state.itemFamily.reviews
});

export default itemFamilySlice.reducer;
